/**
 * Abhianu Spices - variant selection auto-correct.
 *
 * Stock Horizon behaviour: picking an option value that has no matching variant with the other
 * currently selected value leaves the selection unresolved - the price goes blank and the button
 * reads "Unavailable". This module moves the OTHER option(s) to a combination that does exist,
 * preferring in-stock combinations and, among those, the one with the highest total price.
 *
 * Why a document capture listener rather than patching VariantPicker.prototype:
 * Horizon binds its handler with `this.addEventListener('change', this.variantChanged.bind(this))`
 * inside connectedCallback. `.bind()` snapshots the function at connect time, which happens before
 * a deferred script can patch the prototype - patching the prototype therefore has no effect on
 * elements that are already on the page. A capture-phase listener on `document` always runs before
 * the element's own listener, so it can take over reliably.
 *
 * Impossible combinations are not rendered in the DOM at all, so the corrected value may not have
 * a radio input to click. That is why the corrected request is built from the option value id map
 * emitted by variant-main-picker.liquid rather than from the DOM.
 *
 * Only runs on pickers marked data-abh-variant-logic="true" (SP identifier codes).
 */
(function () {
  'use strict';

  if (window.__abhianuVariantAutoCorrect) return;
  window.__abhianuVariantAutoCorrect = true;

  function readData(picker) {
    var script = picker.querySelector('script[data-variant-matrix]');
    if (!script || !script.textContent) return null;
    try {
      var parsed = JSON.parse(script.textContent);
      if (!parsed || !Array.isArray(parsed.variants) || !Array.isArray(parsed.optionValues)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function readOptionNames(picker) {
    try {
      var names = JSON.parse(picker.dataset.optionNames || '[]');
      return Array.isArray(names) ? names : [];
    } catch (error) {
      return [];
    }
  }

  function currentSelection(picker, names) {
    var selection = names.map(function () {
      return null;
    });

    Array.prototype.forEach.call(picker.querySelectorAll('fieldset input:checked'), function (input) {
      var index = names.indexOf(input.dataset.optionName);
      if (index > -1) selection[index] = input.value;
    });

    Array.prototype.forEach.call(picker.querySelectorAll('select'), function (select) {
      var option = select.selectedOptions && select.selectedOptions[0];
      if (!option) return;
      var index = names.indexOf(option.dataset.optionName);
      if (index > -1) selection[index] = select.value;
    });

    return selection;
  }

  function changedOptionName(target) {
    if (target.tagName === 'SELECT') {
      var option = target.selectedOptions && target.selectedOptions[0];
      return option ? option.dataset.optionName : undefined;
    }
    return target.dataset.optionName;
  }

  /**
   * Works out whether the selection the buyer just made is impossible, and if so what to replace
   * it with. Returns null when the stock Horizon path should be left alone.
   */
  function planCorrection(picker, target) {
    if (picker.dataset.abhVariantLogic !== 'true') return null;
    if (picker.dataset.templateProductMatch !== 'true') return null;
    if (target.closest('product-card') || target.closest('quick-add-dialog')) return null;
    if (typeof picker.fetchUpdatedSection !== 'function') return null;

    var data = readData(picker);
    if (!data) return null;

    var names = readOptionNames(picker);
    if (names.length < 2) return null;

    var selection = currentSelection(picker, names);
    var changedIndex = names.indexOf(changedOptionName(target));
    if (changedIndex < 0) return null;
    if (selection[changedIndex] == null) return null;

    var complete = selection.every(function (value) {
      return value != null;
    });

    // A complete, existing combination is the normal case - let Horizon handle it.
    if (complete) {
      var exists = data.variants.some(function (variant) {
        return variant.options.every(function (option, index) {
          return option === selection[index];
        });
      });
      if (exists) return null;
    }

    var candidates = data.variants.filter(function (variant) {
      return variant.options[changedIndex] === selection[changedIndex];
    });
    if (!candidates.length) return null;

    var inStock = candidates.filter(function (variant) {
      return variant.available;
    });
    var pool = inStock.length ? inStock : candidates;

    var best = pool.reduce(function (winner, variant) {
      return variant.price > winner.price ? variant : winner;
    }, pool[0]);

    var ids = best.options.map(function (value, index) {
      var list = data.optionValues[index] || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].v === value) return list[i].id;
      }
      return null;
    });

    for (var j = 0; j < ids.length; j++) {
      if (ids[j] === null || ids[j] === undefined || ids[j] === '') return null;
    }

    var base = (picker.dataset.productUrl || window.location.pathname).split('?')[0];

    return {
      variantId: String(best.id),
      optionValueId: String(ids[changedIndex]),
      requestUrl: base + '?option_values=' + ids.join(','),
      fallbackUrl: base + '?variant=' + best.id,
    };
  }

  function syncHistory(variantId) {
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      if (url.href !== window.location.href) {
        window.history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      /* history is best effort */
    }
  }

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.dataset || typeof target.closest !== 'function') return;
      if (target.tagName !== 'INPUT' && target.tagName !== 'SELECT') return;

      var picker = target.closest('variant-picker');
      if (!picker) return;

      var plan;
      try {
        plan = planCorrection(picker, target);
      } catch (error) {
        console.warn('[abhianu] auto-correct could not plan a correction, using theme default', error);
        return;
      }
      if (!plan) return;

      // From here we own the event: Horizon's own handler would resolve this to "no variant".
      event.stopPropagation();

      try {
        picker.updateSelectedOption(target);
      } catch (error) {
        /* cosmetic only - the morph below restores the correct checked state */
      }

      try {
        picker.fetchUpdatedSection(plan.requestUrl, {
          detail: {
            optionValueId: plan.optionValueId,
            variantId: plan.variantId,
            connectedProductUrl: '',
          },
        });
        syncHistory(plan.variantId);
      } catch (error) {
        // Never leave the buyer looking at a blank price - fall back to a real navigation.
        console.warn('[abhianu] auto-correct fetch failed, navigating instead', error);
        window.location.assign(plan.fallbackUrl);
      }
    },
    true
  );
})();
